import { Injectable, computed, inject, signal } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { AccessToken } from './access-token';

/**
 * The two read-only roles the API recognises. Compared case-insensitively
 * because the value comes verbatim from a database column (HUser.UserRole), not
 * from a constrained enum.
 *
 * The API's global filter (Startup.cs) admits exactly four roles; the two below
 * are refused by its "ClientAdmin" policy, which guards 21 endpoints. Note the
 * API does NOT guard project create, project update, or participant invite, so
 * for those three the read-only rule is enforced here and nowhere else.
 */
const READ_ONLY_ROLES = ['client admin-view only', 'master client admin-view only'];

@Injectable({ providedIn: 'root' })
export class UserPermissionsService {
  private readonly oauth = inject(OAuthService);

  private readonly roleSignal = signal<string | null>(this.readRoleFromToken());

  /** Raw 'type' claim, for display or diagnostics. */
  readonly userRole = computed(() => this.roleSignal());

  readonly isReadOnly = computed(() => {
    const role = (this.roleSignal() ?? '').trim().toLowerCase();
    return READ_ONLY_ROLES.includes(role);
  });

  /**
   * Gate for anything that creates or modifies data. Deliberately derived from
   * isReadOnly rather than from a list of allowed roles: an unrecognised role
   * is refused everything by the API's global filter anyway, so treating it as
   * "can modify" here only produces clearer failures than hiding the whole UI.
   */
  readonly canModify = computed(() => !this.isReadOnly());

  /** Re-read after a token refresh or a login. */
  refresh(): void {
    this.roleSignal.set(this.readRoleFromToken());
  }

  private readRoleFromToken(): string | null {
    return new AccessToken(this.oauth.getAccessToken()).userRole;
  }
}
